/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: LanguagesFormView.tsx
 🗓️ Created: 2025-01-28
 🧩 Description:
    Form view for creating and editing Languages.
    Manages language configuration with General and Settings sections.

 🔧 Notes:
    - Domain-scoped data
    - Supports JSONB settings for FreeSWITCH language features
    - Single page with two sections: General and Settings

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Copy, Languages as LanguagesIcon, Plus, Search, Settings, X } from 'lucide-react';
import { ModuleFormHeader } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { FormBox } from '../../components/ui/FormBox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import RecordingSelect from '../../components/ui/RecordingSelect';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import Toggle from '../../components/ui/Toggle';
import { useLanguageOptions } from '../../hooks/useLanguageOptions';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteLanguageById, navigateToLanguageDuplicate } from './languagesActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { fetchLanguage, createLanguage, updateLanguage, fetchLanguages } from './api/languageApi';
import { Language, LanguageFormState, LanguageSettings } from './types';
import { LanguageSettingsSection } from './components/LanguageSettingsSection';
import { deepClone, generateContextFromName, convertSoundPathToSelectorValue, convertSelectorValueToSoundPath } from './languageUtils';

const DEFAULT_SETTINGS: LanguageSettings = {
  sound_prefix: 'default/us/en/callie',
  announcement_path: null,
  auto_detect: false,
  detect_source: 'none',
  fallback_sound_prefix: 'default/us/en/callie',
  session_only: true,
  revert_after_transfer: true,
  apply_channel_vars: true,
  event_webhook: null,
};

const DEFAULT_FORM: LanguageFormState = {
  name: '',
  context: '',
  destinationModule: '',
  destinationData: '',
  enabled: true,
  settings: DEFAULT_SETTINGS,
};



export default function LanguagesFormView() {
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState<LanguageFormState>(deepClone(DEFAULT_FORM));
  const [originalData, setOriginalData] = useState<LanguageFormState>(deepClone(DEFAULT_FORM));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(isEditMode || Boolean(duplicateId));
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; description?: string | null }>>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const lastSaveTimestamp = useRef(0);
  const quickListLoadingRef = useRef(false);
  const { languageOptions } = useLanguageOptions();

  // Refs for state management
  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  // Stable refs for functions
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    // If we're navigating from edit/create mode to 'new', reset the form
    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(deepClone(DEFAULT_FORM));
      setOriginalData(deepClone(DEFAULT_FORM));
      setErrors({});

      // Clear the reset param from URL to prevent repeated resets
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);
  const soundOptions = useMemo(
    () =>
      languageOptions.flatMap((option) => {
        if (!option.voices?.length) {
          return [
            {
              value: `${option.value}-default`,
              label: option.label,
            },
          ];
        }
        return option.voices.map((voice) => ({
          value: `${option.value}-${voice.value}`,
          label: `${option.label} · ${voice.label ?? voice.value}`,
        }));
      }),
    [languageOptions],
  );

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('languages.editEntry')} - ${formData.name || ''}` : t('languages.createEntry')),
    [isEditMode, t, formData.name],
  );

  const applyLoadedData = useCallback(
    (data: LanguageFormState, { duplicate }: { duplicate: boolean }) => {
      const merged = deepClone(data);
      if (!merged.context) {
        merged.context = generateContextFromName(merged.name);
      }
      if (duplicate) {
        const copySuffix = tRef.current('common:copySuffix');
        merged.name = `${merged.name} ${copySuffix}`.trim();
        merged.context = generateContextFromName(merged.name);
        setTimeout(() => {
          setOriginalData(deepClone(DEFAULT_FORM));
          setFormData(deepClone(merged));
        }, 0);
      } else {
        setTimeout(() => {
          setOriginalData(deepClone(merged));
          setFormData(deepClone(merged));
        }, 0);
      }
    },
    [],
  );

  const applyLoadedDataRef = useRef(applyLoadedData);
  useEffect(() => {
    applyLoadedDataRef.current = applyLoadedData;
  }, [applyLoadedData]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const normalizeResponse = (payload: any): LanguageFormState => ({
    name: payload.name || '',
    context: payload.context || '',
    destinationModule: payload.destinationModule || '',
    destinationData: payload.destinationData || '',
    enabled: Boolean(payload.enabled ?? true),
    settings: {
      ...DEFAULT_SETTINGS,
      ...(payload.settings ?? {}),
    },
  });

  const loadLanguageFn = useCallback(
    async (languageId: string, { duplicate }: { duplicate: boolean }) => {
      if (isLoadingRef.current || lastLoadedIdRef.current === languageId) {
        return;
      }
      isLoadingRef.current = true;
      lastLoadedIdRef.current = languageId;
      setIsLoading(true);
      try {
        const data = await fetchLanguage(languageId);
        const normalized = normalizeResponse(data);
        applyLoadedDataRef.current(normalized, { duplicate });
      } catch (error) {
        lastLoadedIdRef.current = null;
        showErrorRef.current(tRef.current('languages.messages.loadError'));
        navigateRef.current('/pbx/incoming-tools/languages');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [],
  );

  const loadLanguageRef = useRef(loadLanguageFn);
  useEffect(() => {
    loadLanguageRef.current = loadLanguageFn;
  }, [loadLanguageFn]);

  useEffect(() => {
    const loadId = duplicateId || id;
    const idChanged = currentIdRef.current !== loadId;

    if (idChanged) {
      currentIdRef.current = loadId;
    }

    if (isEditMode && id && idChanged) {
      setTimeout(() => {
        loadLanguageRef.current(id, { duplicate: false });
      }, 0);
    } else if (duplicateId && idChanged) {
      setTimeout(() => {
        loadLanguageRef.current(duplicateId, { duplicate: true });
      }, 0);
    } else if (!isEditMode && !duplicateId) {
      setTimeout(() => {
        lastLoadedIdRef.current = null;
      }, 0);
    }
  }, [duplicateId, id, isEditMode]);

  // Reset load guard on unmount — ensures navigating back to the same ID always reloads fresh data
  useEffect(() => {
    return () => {
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (!isEditMode && !duplicateId && formData.name.trim().length > 0 && !isLoadingRef.current) {
      setFormData((prev) => ({
        ...prev,
        context: generateContextFromName(prev.name),
      }));
    }
  }, [duplicateId, formData.name, isEditMode]);

  const handleFieldChange = <K extends keyof LanguageFormState>(
    field: K,
    value: LanguageFormState[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleSettingChange = <K extends keyof LanguageSettings>(
    field: K,
    value: LanguageSettings[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
  };

  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextErrors.name = t('languages.validation.nameRequired');
    }

    if (!formData.destinationModule.trim()) {
      nextErrors.destinationModule = t('languages.validation.destinationRequired');
    }

    if (!formData.destinationData.trim()) {
      nextErrors.destinationData = t('languages.validation.destinationDataRequired');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent): Promise<Language | undefined> => {
      event?.preventDefault();

      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) {
        return undefined;
      }
      lastSaveTimestamp.current = now;

      if (!validateForm()) {
        showErrorRef.current(tRef.current('languages.messages.validationFailed', { defaultValue: tRef.current('common.validationError') }));
        return undefined;
      }
      setIsSaving(true);
      try {
        const payload = {
          name: formData.name.trim(),
          context: formData.context.trim() || generateContextFromName(formData.name),
          destinationModule: formData.destinationModule.trim(),
          destinationData: formData.destinationData.trim(),
          enabled: formData.enabled,
          settings: formData.settings,
        };

        const result = isEditMode
          ? await updateLanguage(id!, payload)
          : await createLanguage(payload);

        showSuccessRef.current(
          isEditMode
            ? tRef.current('languages.messages.updateSuccess')
            : tRef.current('languages.messages.createSuccess'),
        );

        setOriginalData(deepClone(formData));

        // Return saved data for useSaveWithAction
        return result;
      } catch (error) {
        showErrorRef.current(error.message || tRef.current('languages.messages.createError'));
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, id, isEditMode, validateForm],
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: String(id) } as Language;
    }

    return savedData;
  }, [handleSubmit, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/languages',
    newPath: '/pbx/incoming-tools/languages/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Languages use ID for navigation
      return `/pbx/incoming-tools/languages/${savedData.id || savedData.uuid}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'languages:saveAndNewData'
  });

  const handleReset = () => {
    setFormData(deepClone(originalData));
    setErrors({});
  };

  const handleCancel = () => {
    if (isEditMode) {
      handleReset();
    } else {
      navigateRef.current('/pbx/incoming-tools/languages');
    }
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteLanguageById(id);
      showSuccessRef.current(tRef.current('languages.messages.deleteSuccess'));
      navigateRef.current('/pbx/incoming-tools/languages');
    } catch (error) {
      showErrorRef.current(error.message || tRef.current('languages.messages.deleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, isEditMode]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToLanguageDuplicate(navigateRef.current, id);
  }, [id, isEditMode]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const result = await fetchLanguages(
        {
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        undefined,
        'name',
      );
      const items = Array.isArray(result?.data)
        ? (result.data as Array<{ id: number; name: string; description?: string | null }>)
        : [];
      setQuickListItems(items);
    } catch {
      showErrorRef.current(tRef.current('languages.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  if (isLoading) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-4">
      <ModuleFormHeader
        icon={<LanguagesIcon className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('languages.actions.list')}
        onBack={() => navigateRef.current('/pbx/incoming-tools/languages')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('languages.quickList.tooltip', 'Search'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode && !duplicateId
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground text-primary" />,
            tooltip: t('languages.actions.add'),
            onClick: () => navigateRef.current('/pbx/incoming-tools/languages/new'),
          },
        ]}
      />

      <form onSubmit={handleSubmit} className="mt-16 space-y-6">
        <FormBox title={t('languages.sections.general')} icon={<LanguagesIcon className="h-5 w-5" />}>
          <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Label htmlFor="name">
              <SimpleTooltip content={t('languages.fields.name.tooltip')}>
                <span>
                  {t('languages.fields.name.label')}
                  <span className="ml-1 text-red-500">*</span>
                </span>
              </SimpleTooltip>
            </Label>
            <div>
              <Input
                id="name"
                value={formData.name}
                onChange={(event) => {
                  const nextName = event.target.value;
                  handleFieldChange('name', nextName);
                  handleFieldChange('context', generateContextFromName(nextName));
                }}
                placeholder={t('languages.fields.name.placeholder')}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
            </div>

            <Label htmlFor="announcementPath">
              <SimpleTooltip content={t('languages.settings.announcementPath.tooltip')}>
                {t('languages.settings.announcementPath.label')}
              </SimpleTooltip>
            </Label>
            <div>
              <RecordingSelect
                value={formData.settings.announcement_path ?? ''}
                onChange={(value) => handleSettingChange('announcement_path', value || null)}
                placeholder={t('languages.settings.announcementPath.placeholder')}
                returnType="id"
                showPlayButton
              />
            </div>

            <Label>
              <SimpleTooltip content={t('languages.fields.destination.tooltip')}>
                <span>
                  {t('languages.fields.destination.label')}
                  <span className="ml-1 text-red-500">*</span>
                </span>
              </SimpleTooltip>
            </Label>
            <div className="col-span-3">
              <ModuleDestinationSelect
                moduleValue={formData.destinationModule}
                destinationValue={formData.destinationData}
                onModuleChange={(value) => handleFieldChange('destinationModule', value || '')}
                onDestinationChange={(value) => handleFieldChange('destinationData', value || '')}
                modulePlaceholder={t('languages.fields.destination.modulePlaceholder')}
                destinationPlaceholder={t('languages.fields.destination.destinationPlaceholder')}
              />
              {(errors.destinationModule || errors.destinationData) && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.destinationModule || errors.destinationData}
                </p>
              )}
            </div>

            <Label htmlFor="enabled">
              <SimpleTooltip content={t('languages.fields.enabled.tooltip')}>
                {t('languages.fields.enabled.label')}
              </SimpleTooltip>
            </Label>
            <div>
              <Toggle
                value={formData.enabled}
                onChange={(value) => handleFieldChange('enabled', value)}
              />
            </div>
          </div>
        </FormBox>

        <LanguageSettingsSection
          settings={formData.settings}
          soundOptions={soundOptions}
          languageOptions={languageOptions}
          onSettingChange={handleSettingChange}
          t={t}
        />

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={handleCancel}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isEditMode={isEditMode}
          isSaving={isSaving}
          isLoading={isLoading}
          saveText={t('languages.actions.save')}
          cancelText={t('languages.actions.cancel')}
          deleteText={t('languages.actions.delete')}
          useSaveDropdown={true}
          saveDropdownStorageKey="languages:saveActionPreference"
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('languages.messages.deleteTitle')}
        message={t('languages.messages.confirmDelete', { name: formData.name || '' })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('languages.quickList.title', 'Languages')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigateRef.current(`/pbx/incoming-tools/languages/${item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigateRef.current(`/pbx/incoming-tools/languages/${item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('languages.quickList.searchPlaceholder', 'Search by name or description')}
        emptyMessage={t('languages.quickList.empty', 'No languages found')}
        headerIcon={<LanguagesIcon className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}

